// Bundle builder custom element
if (!customElements.get('ks-bundle-builder')) {
  class KsBundleBuilder extends HTMLElement {
    connectedCallback() {
      // Stage filters
      this.stageFilterButtons = Array.from(this.querySelectorAll('[data-ks-stage-filter]'));
      this.productGroups = Array.from(this.querySelectorAll('[data-ks-product-group]'));
      this.updateStageFilterIndicator = this.updateStageFilterIndicator.bind(this);

      this.stageFilterButtons.forEach((button) => {
        button.addEventListener('click', () => {
          this.setActiveStage(button.dataset.ksStageFilter);
        });
      });

      this.updateStageFilterIndicator();
      window.addEventListener('resize', this.updateStageFilterIndicator);

      // Bundle product card actions
      this.bundleStorageKey = 'ks-bundle-builder-v1';
      this.bundleStateVersion = 1;
      this.bundleStateLifetime = 14 * 24 * 60 * 60 * 1000;
      this.bundleId = '';
      this.bundleItems = new Map();
      this.bundleIsSubmitting = false;

      // Bundle contents modal
      this.bundleContentsModal = this.querySelector('[data-ks-bundle-contents-modal]');
      this.handleBundleContentsClick = this.handleBundleContentsClick.bind(this);
      this.addEventListener('click', this.handleBundleContentsClick);

      // Bundle cart actions
      this.handleBundleSubmit = this.handleBundleSubmit.bind(this);
      this.getBundleSubmitButtons().forEach((button) => {
        button.addEventListener('click', this.handleBundleSubmit);
      });

      this.querySelectorAll('[data-ks-bundle-add]').forEach((button) => {
        button.addEventListener('click', () => this.addBundleItem(button));
      });

      this.quickViewObservers = [];

      this.querySelectorAll('.ks-bundle-quick-view').forEach((quickView) => {
        quickView.addEventListener('dialog:before-show', () => this.prepareBundleQuickView(quickView));
        quickView.addEventListener('variant:change', () => {
          this.updateQuickViewBundleAction(quickView);
          this.updateQuickViewDetailsLink(quickView);
        });

        const quickViewObserver = new MutationObserver(() => this.prepareBundleQuickView(quickView));

        quickViewObserver.observe(quickView, { childList: true, subtree: true });
        this.quickViewObservers.push(quickViewObserver);
      });

      // Mobile bundle summary spacing
      this.mainSummary = this.querySelector('[data-ks-bundle-summary-main]');
      this.mobileSummary = this.querySelector('[data-ks-bundle-summary-mobile]');

      if (this.mobileSummary instanceof HTMLElement) {
        this.mobileSummaryResizeObserver = new ResizeObserver((entries) => {
          const mobileSummaryHeight = entries[0]?.borderBoxSize?.[0]?.blockSize || this.mobileSummary.offsetHeight;

          this.style.setProperty('--ks-bundle-mobile-summary-height', `${mobileSummaryHeight}px`);
        });
        this.mobileSummaryResizeObserver.observe(this.mobileSummary);
      }

      if (this.mainSummary instanceof HTMLElement && this.mobileSummary instanceof HTMLElement) {
        this.mainSummaryObserver = new IntersectionObserver((entries) => {
          const mainSummaryEntry = entries[0];
          const hasPassedMainSummary = !mainSummaryEntry.isIntersecting && mainSummaryEntry.boundingClientRect.bottom <= 0;

          this.mobileSummary.dataset.ksMobileSummaryVisible = String(hasPassedMainSummary);
        });
        this.mainSummaryObserver.observe(this.mainSummary);
      }

      // Bundle summary
      this.restoreBundleState();
      this.observeBundleAnalyticsView();

      // Mark the section as initialized for upcoming bundle functionality.
      this.dataset.initialized = 'true';
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.updateStageFilterIndicator);
      this.mobileSummaryResizeObserver?.disconnect();
      this.mainSummaryObserver?.disconnect();
      this.bundleAnalyticsViewObserver?.disconnect();
      this.quickViewObservers?.forEach((observer) => observer.disconnect());
      this.removeEventListener('click', this.handleBundleContentsClick);
      this.getBundleSubmitButtons().forEach((button) => {
        button.removeEventListener('click', this.handleBundleSubmit);
      });
    }

    setActiveStage(stage) {
      this.stageFilterButtons.forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.ksStageFilter === stage));
      });

      this.productGroups.forEach((group) => {
        group.hidden = stage !== 'all' && group.dataset.ksProductGroup !== stage;
      });

      this.updateStageFilterIndicator();
    }

    updateStageFilterIndicator() {
      const filterGroup = this.querySelector('.ks-bundle-builder-filter-buttons');
      const activeButton = filterGroup?.querySelector('[data-ks-stage-filter][aria-pressed="true"]');

      if (!(filterGroup instanceof HTMLElement) || !(activeButton instanceof HTMLElement)) return;

      filterGroup.style.setProperty('--ks-filter-active-left', `${activeButton.offsetLeft}px`);
      filterGroup.style.setProperty('--ks-filter-active-top', `${activeButton.offsetTop}px`);
      filterGroup.style.setProperty('--ks-filter-active-width', `${activeButton.offsetWidth}px`);
      filterGroup.style.setProperty('--ks-filter-active-height', `${activeButton.offsetHeight}px`);
      filterGroup.style.setProperty('--ks-filter-active-opacity', '1');
    }

    addBundleItem(button) {
      if (!(button instanceof HTMLButtonElement)) return;
      if (button.disabled) return;

      const bundleItem = this.getBundleItemFromElement(button);

      if (!bundleItem) return;

      this.addBundleItemToState(bundleItem);
      this.showBundleCardFeedback(button);
    }

    // Brief confirmation on image actions, matching the quick-view feedback.
    showBundleCardFeedback(button) {
      const originalLabel = button.getAttribute('aria-label');
      const quickView = button.closest('product-card')?.querySelector('.ks-bundle-quick-view');

      button.dataset.ksBundleFeedback = 'added';
      button.setAttribute('aria-label', quickView?.dataset.ksAddedLabel || 'Added to bundle');
      button.disabled = true;

      window.setTimeout(() => {
        delete button.dataset.ksBundleFeedback;
        if (originalLabel === null) {
          button.removeAttribute('aria-label');
        } else {
          button.setAttribute('aria-label', originalLabel);
        }
        button.disabled = false;
      }, 650);
    }

    addBundleItemToState(bundleItem) {
      const previousAnalytics = this.getBundleAnalyticsData();
      const existingItem = this.bundleItems.get(bundleItem.variantId);
      const addedQuantity = Math.max(1, Math.floor(Number(bundleItem.quantity) || 1));

      this.ensureBundleId();
      this.bundleItems.set(bundleItem.variantId, {
        ...bundleItem,
        quantity: (existingItem?.quantity || 0) + addedQuantity
      });

      this.updateBundleState();
      this.trackBundleItemChange(bundleItem, addedQuantity, previousAnalytics);
    }

    // Custom customer events. Pixel subscribers control consent and GA4 delivery.
    publishBundleAnalytics(name, data = this.getBundleAnalyticsData()) {
      if (window.Shopify?.designMode) return;

      try {
        const analytics = window.Shopify?.analytics;
        if (typeof analytics?.publish !== 'function') return;
        Promise.resolve(analytics.publish(name, data)).catch(() => {});
      } catch {
        // Tracking must never interrupt product selection or cart submission.
      }
    }

    observeBundleAnalyticsView() {
      if (this.bundleAnalyticsViewed) return;

      this.bundleAnalyticsViewObserver = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.bundleAnalyticsViewed = true;
        this.bundleAnalyticsViewObserver.disconnect();
        this.publishBundleAnalytics('ks-bundle-viewed');
      });
      this.bundleAnalyticsViewObserver.observe(this);
    }

    getBundleAnalyticsData() {
      const items = Array.from(this.bundleItems.values());
      const count = items.reduce((total, item) => total + item.quantity, 0);
      const subtotal = items.reduce((total, item) => total + item.unitPrice * item.quantity, 0);
      const discount = this.getBundleDiscountPercentage(count);

      return {
        'ks-bundle-id': this.bundleId,
        'ks-theme-id': String(window.Shopify?.theme?.id || ''),
        'ks-currency': this.getBundleCurrency(),
        'ks-item-count': count,
        'ks-subtotal': subtotal / 100,
        'ks-estimated-total': (subtotal - Math.round(subtotal * discount / 100)) / 100,
        'ks-discount-percentage': discount
      };
    }

    trackBundleItemChange(item, quantityChange, previous) {
      const current = this.getBundleAnalyticsData();
      this.publishBundleAnalytics(quantityChange > 0 ? 'ks-bundle-product-added' : 'ks-bundle-product-removed', {
        ...current,
        'ks-bundle-id': current['ks-bundle-id'] || previous['ks-bundle-id'],
        'ks-product-id': String(item.productId),
        'ks-variant-id': String(item.variantId),
        'ks-quantity': Math.abs(quantityChange),
        'ks-unit-price': item.unitPrice / 100
      });

      // Read one progress component so desktop/mobile copies cannot double count.
      const tiers = this.querySelector('[data-ks-bundle-progress]')?.querySelectorAll('[data-ks-bundle-tier]') || [];
      tiers.forEach((tier) => {
        const threshold = Number(tier.dataset.ksBundleTier);
        if (threshold <= previous['ks-item-count'] || threshold > current['ks-item-count']) return;
        this.publishBundleAnalytics('ks-bundle-discount-reached', {
          ...current,
          'ks-tier-item-count': threshold,
          'ks-tier-discount-percentage': Number(tier.dataset.ksBundleDiscount) || 0
        });
      });
    }

    removeBundleItem(variantId) {
      const item = this.bundleItems.get(variantId);
      if (!item) return;
      const previous = this.getBundleAnalyticsData();
      this.bundleItems.delete(variantId);
      this.updateBundleState();
      this.trackBundleItemChange(item, -item.quantity, previous);
    }

    getBundleItemFromElement(element) {
      const variantId = element.dataset.ksVariantId;

      if (!variantId) return null;

      let selectedOptions = [];

      try {
        const parsedOptions = JSON.parse(element.dataset.ksSelectedOptions || '[]');

        if (Array.isArray(parsedOptions)) {
          selectedOptions = parsedOptions.map((option) => String(option));
        }
      } catch {
        selectedOptions = [];
      }

      return {
        variantId,
        productId: element.dataset.ksProductId || '',
        productHandle: element.dataset.ksProductHandle || '',
        productTitle: element.dataset.ksProductTitle || '',
        variantTitle: element.dataset.ksVariantTitle || '',
        selectedOptions,
        imageUrl: element.dataset.ksImageUrl || '',
        imageAlt: element.dataset.ksImageAlt || '',
        unitPrice: Math.max(0, Math.round(Number(element.dataset.ksVariantPrice) || 0)),
        quantity: 1
      };
    }

    updateBundleState(shouldSave = true) {
      const bundleItems = Array.from(this.bundleItems.values());
      const itemCount = bundleItems.reduce((total, item) => total + item.quantity, 0);
      const subtotal = bundleItems.reduce((total, item) => total + item.unitPrice * item.quantity, 0);

      this.renderVisibleBundleProducts(bundleItems);
      this.renderBundleContents(bundleItems);
      this.updateBundleSummary(itemCount, subtotal);

      if (shouldSave) {
        this.saveBundleState(bundleItems);
      }

      this.dispatchEvent(new CustomEvent('ks-bundle:change', {
        bubbles: true,
        detail: { items: bundleItems }
      }));
    }

    handleBundleContentsClick(event) {
      if (this.bundleIsSubmitting) return;

      const actionButton = event.target.closest('[data-ks-bundle-item-action]');

      if (!(actionButton instanceof HTMLButtonElement)) return;

      const variantId = actionButton.dataset.ksVariantId;
      const action = actionButton.dataset.ksBundleItemAction;

      if (!variantId || !this.bundleItems.has(variantId)) return;

      this.bundleContentsFocusTarget = {
        variantId,
        action,
        list: actionButton.closest('[data-ks-bundle-summary-products], [data-ks-bundle-contents-list]')
      };

      if (action === 'remove') {
        this.removeBundleItem(variantId);
        return;
      }

      const item = this.bundleItems.get(variantId);
      if (action === 'remove-one' && item.quantity === 1) {
        this.removeBundleItem(variantId);
        return;
      }
      const quantityChange = action === 'increase' ? 1 : -1;

      this.setBundleItemQuantity(variantId, item.quantity + quantityChange);
    }

    setBundleItemQuantity(variantId, quantity) {
      const item = this.bundleItems.get(variantId);

      if (!item) return;

      const normalizedQuantity = Math.max(1, Math.floor(Number(quantity) || 1));

      if (item.quantity === normalizedQuantity) return;

      const previousAnalytics = this.getBundleAnalyticsData();
      this.bundleItems.set(variantId, {
        ...item,
        quantity: normalizedQuantity
      });
      this.updateBundleState();
      this.trackBundleItemChange(item, normalizedQuantity - item.quantity, previousAnalytics);
    }

    // Show one thumbnail per unit in the main summary.
    renderVisibleBundleProducts(bundleItems) {
      const products = this.querySelector('[data-ks-bundle-summary-products]');
      if (!products) return;

      const fragment = document.createDocumentFragment();
      bundleItems.forEach((item) => {
        const label = [item.productTitle, item.variantTitle === 'Default Title' ? '' : item.variantTitle].filter(Boolean).join(' — ');
        for (let unit = 0; unit < item.quantity; unit += 1) {
          const tile = document.createElement('div');
          const remove = document.createElement('button');
          tile.className = 'ks-bundle-summary-thumbnail';
          tile.title = label;

          if (item.imageUrl) {
            const image = document.createElement('img');
            image.src = item.imageUrl;
            image.alt = label;
            tile.append(image);
          } else {
            const fallback = document.createElement('span');
            fallback.className = 'ks-bundle-summary-thumbnail-fallback';
            fallback.textContent = label;
            tile.append(fallback);
          }

          remove.type = 'button';
          remove.className = 'ks-bundle-summary-thumbnail-remove';
          remove.dataset.ksBundleItemAction = 'remove-one';
          remove.dataset.ksVariantId = item.variantId;
          remove.textContent = '×';
          remove.setAttribute('aria-label', this.getBundleContentsLabel('ksRemoveLabel', label));
          tile.append(remove);
          fragment.append(tile);
        }
      });

      products.replaceChildren(fragment);
      products.hidden = bundleItems.length === 0;
      this.restoreBundleContentsFocus(products);
    }

    renderBundleContents(bundleItems) {
      if (!(this.bundleContentsModal instanceof HTMLElement)) return;

      const contentsList = this.bundleContentsModal.querySelector('[data-ks-bundle-contents-list]');
      const emptyMessage = this.bundleContentsModal.querySelector('[data-ks-bundle-contents-empty]');
      const contentsSummary = this.bundleContentsModal.querySelector('[data-ks-bundle-contents-summary]');
      const contentsCount = this.bundleContentsModal.querySelector('[data-ks-bundle-contents-count]');
      const hasItems = bundleItems.length > 0;
      const itemCount = bundleItems.reduce((total, item) => total + item.quantity, 0);

      if (!(contentsList instanceof HTMLElement)) return;

      const fragment = document.createDocumentFragment();

      bundleItems.forEach((item) => {
        fragment.append(this.createBundleContentsItem(item));
      });

      contentsList.replaceChildren(fragment);

      if (emptyMessage instanceof HTMLElement) {
        emptyMessage.hidden = hasItems;
      }

      if (contentsSummary instanceof HTMLElement) {
        contentsSummary.hidden = !hasItems;
      }

      if (contentsCount instanceof HTMLElement) {
        contentsCount.textContent = String(itemCount);
      }

      this.restoreBundleContentsFocus(contentsList);
    }

    restoreBundleContentsFocus(contentsList) {
      if (!this.bundleContentsFocusTarget) return;
      if (this.bundleContentsFocusTarget.list !== contentsList) return;

      const { variantId, action } = this.bundleContentsFocusTarget;
      const escapedVariantId = CSS.escape(variantId);
      let focusTarget = null;

      focusTarget = contentsList.querySelector(
        `[data-ks-bundle-item-action="${action}"][data-ks-variant-id="${escapedVariantId}"]`
      );

      if (focusTarget instanceof HTMLButtonElement && focusTarget.disabled) {
        focusTarget = contentsList.querySelector(
          `[data-ks-bundle-item-action="increase"][data-ks-variant-id="${escapedVariantId}"]`
        );
      }

      focusTarget ||= contentsList.querySelector('[data-ks-bundle-item-action="remove-one"]');
      focusTarget ||= contentsList.querySelector('[data-ks-bundle-item-action="remove"]');
      focusTarget ||= contentsList.hasAttribute('data-ks-bundle-summary-products')
        ? this.mainSummary.querySelector('[data-ks-bundle-submit]')
        : this.bundleContentsModal;
      if (focusTarget?.disabled) focusTarget = this.mainSummary.querySelector('h2') || this.mainSummary;
      if (!focusTarget.hasAttribute('tabindex') && !(focusTarget instanceof HTMLButtonElement)) {
        focusTarget.setAttribute('tabindex', '-1');
      }
      this.bundleContentsFocusTarget = null;
      focusTarget.focus({ preventScroll: true });
    }

    createBundleContentsItem(item) {
      const itemElement = document.createElement('article');
      const mediaElement = document.createElement('div');
      const infoElement = document.createElement('div');
      const titleElement = document.createElement('h3');
      const variantElement = document.createElement('p');
      const unitPriceElement = document.createElement('p');
      const lineTotalElement = document.createElement('strong');
      const actionsElement = document.createElement('div');
      const quantityElement = document.createElement('div');
      const decreaseButton = this.createBundleQuantityButton(item, 'decrease', '−');
      const quantityInput = document.createElement('input');
      const increaseButton = this.createBundleQuantityButton(item, 'increase', '+');
      const removeButton = document.createElement('button');

      itemElement.className = 'ks-bundle-contents-item';
      itemElement.dataset.ksBundleContentsItem = item.variantId;
      mediaElement.className = 'ks-bundle-contents-media';

      if (item.imageUrl) {
        const imageElement = document.createElement('img');

        imageElement.className = 'ks-bundle-contents-image';
        imageElement.src = item.imageUrl;
        imageElement.alt = item.imageAlt || item.productTitle;
        imageElement.loading = 'lazy';
        mediaElement.append(imageElement);
      }

      infoElement.className = 'ks-bundle-contents-info';
      titleElement.className = 'ks-bundle-contents-title h6';
      titleElement.textContent = item.productTitle;
      infoElement.append(titleElement);

      if (item.variantTitle && item.variantTitle !== 'Default Title') {
        variantElement.className = 'ks-bundle-contents-variant text-sm text-subdued';
        variantElement.textContent = item.variantTitle;
        infoElement.append(variantElement);
      }

      unitPriceElement.className = 'ks-bundle-contents-unit-price text-sm';
      unitPriceElement.textContent = this.formatBundleMoney(item.unitPrice);
      infoElement.append(unitPriceElement);

      lineTotalElement.className = 'ks-bundle-contents-line-total';
      lineTotalElement.textContent = this.formatBundleMoney(item.unitPrice * item.quantity);
      actionsElement.className = 'ks-bundle-contents-item-actions';
      quantityElement.className = 'ks-bundle-contents-quantity quantity-selector quantity-selector--sm';
      quantityInput.className = 'quantity-selector__input text-sm';
      quantityInput.type = 'number';
      quantityInput.inputMode = 'numeric';
      quantityInput.min = '1';
      quantityInput.step = '1';
      quantityInput.value = String(item.quantity);
      quantityInput.readOnly = true;
      quantityInput.dataset.ksBundleQuantityInput = '';
      quantityInput.dataset.ksVariantId = item.variantId;
      quantityInput.setAttribute('aria-label', this.getBundleContentsLabel('ksQuantityLabel', item.productTitle));
      decreaseButton.disabled = item.quantity <= 1;
      quantityElement.append(decreaseButton, quantityInput, increaseButton);

      removeButton.className = 'ks-bundle-contents-remove link text-sm';
      removeButton.type = 'button';
      removeButton.textContent = this.bundleContentsModal.dataset.ksRemoveText || 'Remove';
      removeButton.dataset.ksBundleItemAction = 'remove';
      removeButton.dataset.ksVariantId = item.variantId;
      removeButton.setAttribute('aria-label', this.getBundleContentsLabel('ksRemoveLabel', item.productTitle));
      actionsElement.append(quantityElement, removeButton);
      itemElement.append(mediaElement, infoElement, lineTotalElement, actionsElement);

      return itemElement;
    }

    createBundleQuantityButton(item, action, symbol) {
      const button = document.createElement('button');
      const symbolElement = document.createElement('span');
      const labelName = action === 'increase' ? 'ksIncreaseLabel' : 'ksDecreaseLabel';

      button.className = 'quantity-selector__button';
      button.type = 'button';
      button.dataset.ksBundleItemAction = action;
      button.dataset.ksVariantId = item.variantId;
      button.setAttribute('aria-label', this.getBundleContentsLabel(labelName, item.productTitle));
      symbolElement.className = 'ks-bundle-contents-quantity-symbol';
      symbolElement.setAttribute('aria-hidden', 'true');
      symbolElement.textContent = symbol;
      button.append(symbolElement);

      return button;
    }

    getBundleContentsLabel(labelName, productTitle) {
      const labelTemplate = this.bundleContentsModal?.dataset[labelName] || '';

      return labelTemplate.replaceAll('[product]', productTitle);
    }

    async handleBundleSubmit(event) {
      const submitButton = event.currentTarget;

      if (!(submitButton instanceof HTMLButtonElement)) return;
      if (this.bundleIsSubmitting || this.bundleItems.size === 0) return;

      const bundleAction = submitButton.dataset.ksBundleAction === 'checkout' ? 'checkout' : 'cart';
      const lineItems = this.getBundleLineItems(bundleAction === 'cart');
      const analyticsData = { ...this.getBundleAnalyticsData(), 'ks-action': bundleAction };
      this.publishBundleAnalytics('ks-bundle-submit-attempted', analyticsData);

      if (lineItems.length !== this.bundleItems.size) {
        this.publishBundleAnalytics('ks-bundle-submit-failed', { ...analyticsData, 'ks-failure-reason': 'invalid-variant' });
        this.showBundleSubmitError(submitButton);
        return;
      }

      let themeLoadingStarted = false;
      let cartItemsAdded = false;
      let failureReason = 'network-error';

      this.clearBundleSubmitErrors();
      this.setBundleSubmitLoading(true);

      try {
        if (bundleAction === 'checkout') {
          this.publishBundleAnalytics('ks-bundle-checkout-redirected', analyticsData);
          window.location.assign(this.getBundleCheckoutUrl(lineItems));
          this.clearStoredBundleState();
          return;
        }

        const shouldOpenCartDrawer = window.themeVariables?.settings?.cartType === 'drawer'
          && window.themeVariables?.settings?.pageType !== 'cart';
        const sectionsToBundle = [];

        if (shouldOpenCartDrawer) {
          document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle }
          }));
        }

        const requestBody = { items: lineItems };

        if (sectionsToBundle.length > 0) {
          requestBody.sections = sectionsToBundle.join(',');
          requestBody.sections_url = window.location.pathname;
        }

        document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));
        themeLoadingStarted = true;

        const response = await fetch(`${this.getShopifyRoot()}cart/add.js`, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          body: JSON.stringify(requestBody)
        });
        const responseData = await response.json().catch(() => ({}));

        if (!response.ok) {
          failureReason = 'cart-rejected';
          throw new Error(responseData.description || responseData.message || 'Bundle cart request failed');
        }

        cartItemsAdded = true;
        this.publishBundleAnalytics('ks-bundle-cart-added', analyticsData);

        if (shouldOpenCartDrawer && sectionsToBundle.length > 0 && responseData.sections) {
          const cartResponse = await fetch(`${this.getShopifyRoot()}cart.js`);

          if (!cartResponse.ok) {
            throw new Error('Bundle cart refresh failed');
          }

          const cartContent = await cartResponse.json();
          const addedItems = Array.isArray(responseData.items) ? responseData.items : [responseData];

          cartContent.sections = responseData.sections;

          if (this.bundleContentsModal?.hasAttribute('open')) {
            await this.bundleContentsModal.hide();
          }

          this.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: addedItems,
              cart: cartContent
            }
          }));
          document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cartContent
            }
          }));

          this.bundleItems.clear();
          this.bundleIsSubmitting = false;
          this.updateBundleState();
          this.clearBundleSubmitErrors();
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
          themeLoadingStarted = false;
          return;
        }

        window.location.assign(`${this.getShopifyRoot()}cart`);
        this.clearStoredBundleState();
      } catch (error) {
        console.error('Unable to submit bundle:', error);

        if (cartItemsAdded) {
          window.location.assign(`${this.getShopifyRoot()}cart`);
          this.clearStoredBundleState();
          return;
        }

        if (themeLoadingStarted) {
          document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
        }

        this.publishBundleAnalytics('ks-bundle-submit-failed', { ...analyticsData, 'ks-failure-reason': failureReason });
        this.setBundleSubmitLoading(false);
        this.showBundleSubmitError(submitButton, error.message);
      }
    }

    getBundleLineItems(includeBundleProperties = false) {
      const bundleItems = Array.from(this.bundleItems.values());
      const itemCount = bundleItems.reduce((total, item) => total + item.quantity, 0);
      const bundleProperties = includeBundleProperties
        ? {
            _ks_bundle_id: this.ensureBundleId(),
            _ks_bundle_source: 'ks-bundle-builder',
            _ks_bundle_item_count: String(itemCount),
            _ks_bundle_discount_percentage: String(this.getBundleDiscountPercentage(itemCount))
          }
        : null;

      return bundleItems
        .filter((item) => /^\d+$/.test(String(item.variantId)))
        .map((item) => {
          const lineItem = {
            id: String(item.variantId),
            quantity: Math.max(1, Math.floor(Number(item.quantity) || 1))
          };

          if (bundleProperties) {
            lineItem.properties = { ...bundleProperties };
          }

          return lineItem;
        });
    }

    getBundleDiscountPercentage(itemCount) {
      const progress = this.querySelector('[data-ks-bundle-progress]');

      if (!(progress instanceof HTMLElement)) return 0;

      const reachedTier = Array.from(progress.querySelectorAll('[data-ks-bundle-tier]'))
        .findLast((tier) => Number(tier.dataset.ksBundleTier) <= itemCount);

      return Math.max(0, Number(reachedTier?.dataset.ksBundleDiscount) || 0);
    }

    ensureBundleId() {
      if (!/^ks-[a-f0-9]{16}$/.test(this.bundleId)) {
        this.bundleId = `ks-${crypto.randomUUID().replaceAll('-', '').slice(0, 16)}`;
      }

      return this.bundleId;
    }

    getShopifyRoot() {
      const root = window.Shopify?.routes?.root || '/';

      return root.endsWith('/') ? root : `${root}/`;
    }

    getBundleCheckoutUrl(lineItems) {
      const checkoutLines = lineItems.map((item) => `${item.id}:${item.quantity}`).join(',');

      return `${this.getShopifyRoot()}cart/${checkoutLines}`;
    }

    getBundleSubmitButtons() {
      const submitButtons = new Set(this.querySelectorAll('[data-ks-bundle-submit]'));

      this.bundleContentsModal?.querySelectorAll('[data-ks-bundle-submit]').forEach((button) => {
        submitButtons.add(button);
      });

      return submitButtons;
    }

    getBundleSubmitErrors() {
      const errorMessages = new Set(this.querySelectorAll('[data-ks-bundle-submit-error]'));

      this.bundleContentsModal?.querySelectorAll('[data-ks-bundle-submit-error]').forEach((message) => {
        errorMessages.add(message);
      });

      return errorMessages;
    }

    setBundleSubmitLoading(isLoading) {
      this.bundleIsSubmitting = isLoading;

      this.getBundleSubmitButtons().forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        button.disabled = isLoading || this.bundleItems.size === 0;
        button.toggleAttribute('aria-busy', isLoading);
      });
    }

    clearBundleSubmitErrors() {
      this.getBundleSubmitErrors().forEach((message) => {
        if (!(message instanceof HTMLElement)) return;

        message.textContent = '';
        message.hidden = true;
      });
    }

    showBundleSubmitError(submitButton, errorMessage = '') {
      this.clearBundleSubmitErrors();

      const message = submitButton.nextElementSibling;

      if (!(message instanceof HTMLElement) || !message.matches('[data-ks-bundle-submit-error]')) return;

      message.textContent = errorMessage || this.dataset.ksCartError || 'Unable to add bundle.';
      message.hidden = false;
    }

    saveBundleState(bundleItems) {
      try {
        if (bundleItems.length === 0) {
          this.bundleId = '';
          localStorage.removeItem(this.bundleStorageKey);
          return;
        }

        localStorage.setItem(this.bundleStorageKey, JSON.stringify({
          version: this.bundleStateVersion,
          updatedAt: Date.now(),
          currency: this.getBundleCurrency(),
          bundleId: this.ensureBundleId(),
          items: bundleItems
        }));
      } catch {
        // The bundle remains usable when storage is unavailable or full.
      }
    }

    restoreBundleState() {
      let storedState = null;

      try {
        storedState = JSON.parse(localStorage.getItem(this.bundleStorageKey) || 'null');
      } catch {
        this.clearStoredBundleState();
      }

      const stateIsCurrent = storedState?.version === this.bundleStateVersion;
      const stateIsFresh = Number(storedState?.updatedAt) > Date.now() - this.bundleStateLifetime;

      if (!stateIsCurrent || !stateIsFresh || !Array.isArray(storedState?.items)) {
        this.clearStoredBundleState();
        this.updateBundleState(false);
        return;
      }

      const activeCurrency = this.getBundleCurrency();
      const currencyMatches = storedState.currency === activeCurrency;

      if (/^ks-[a-f0-9]{16}$/.test(String(storedState.bundleId || ''))) {
        this.bundleId = storedState.bundleId;
      }

      storedState.items.forEach((storedItem) => {
        const variantId = String(storedItem?.variantId || '');

        if (!variantId) return;

        const currentButton = this.querySelector(`[data-ks-bundle-add][data-ks-variant-id="${CSS.escape(variantId)}"]`);
        const currentItem = currentButton instanceof HTMLButtonElement ? this.getBundleItemFromElement(currentButton) : null;
        const restoredItem = currentItem || (currencyMatches ? this.normalizeStoredBundleItem(storedItem) : null);

        if (!restoredItem) return;

        const existingItem = this.bundleItems.get(variantId);
        const restoredQuantity = Math.max(1, Math.floor(Number(storedItem.quantity) || 1));

        this.bundleItems.set(variantId, {
          ...restoredItem,
          quantity: (existingItem?.quantity || 0) + restoredQuantity
        });
      });

      this.updateBundleState();
    }

    normalizeStoredBundleItem(storedItem) {
      const variantId = String(storedItem?.variantId || '');

      if (!variantId) return null;

      return {
        variantId,
        productId: String(storedItem.productId || ''),
        productHandle: String(storedItem.productHandle || ''),
        productTitle: String(storedItem.productTitle || ''),
        variantTitle: String(storedItem.variantTitle || ''),
        selectedOptions: Array.isArray(storedItem.selectedOptions)
          ? storedItem.selectedOptions.map((option) => String(option))
          : [],
        imageUrl: String(storedItem.imageUrl || ''),
        imageAlt: String(storedItem.imageAlt || ''),
        unitPrice: Math.max(0, Math.round(Number(storedItem.unitPrice) || 0)),
        quantity: 1
      };
    }

    clearStoredBundleState() {
      this.bundleId = '';

      try {
        localStorage.removeItem(this.bundleStorageKey);
      } catch {
        // Ignore storage restrictions because they should not block the builder.
      }
    }

    prepareBundleQuickView(quickView) {
      this.removeQuickViewProductLinks(quickView);
      this.renderQuickViewBundleAction(quickView);
    }

    removeQuickViewProductLinks(quickView) {
      const viewDetailsLink = quickView.querySelector('.quick-buy-modal__view-more');

      quickView.querySelectorAll('a[href*="/products/"]').forEach((productLink) => {
        if (productLink === viewDetailsLink) return;
        if (productLink.closest('[data-ks-bundle-description]')) return;

        const productText = document.createElement('span');

        productText.className = productLink.className;
        productText.textContent = productLink.textContent;
        productLink.replaceWith(productText);
      });

      if (!(viewDetailsLink instanceof HTMLAnchorElement)) return;

      if (!viewDetailsLink.dataset.ksBundleProductUrl) {
        viewDetailsLink.dataset.ksBundleProductUrl = viewDetailsLink.getAttribute('href') || '';
      }

      if (!viewDetailsLink.hasAttribute('data-ks-bundle-details-prepared')) {
        const detailsLabel = quickView.dataset.ksFullDetailsLabel || viewDetailsLink.textContent;
        const arrow = document.createElement('span');

        arrow.className = 'ks-bundle-quick-view-details-arrow';
        arrow.setAttribute('aria-hidden', 'true');
        arrow.textContent = '→';
        viewDetailsLink.replaceChildren(document.createTextNode(detailsLabel), arrow);
        viewDetailsLink.dataset.ksBundleDetailsPrepared = '';
      }

      viewDetailsLink.classList.remove('sm-max:hidden');
      viewDetailsLink.classList.add('ks-bundle-quick-view-details');
      viewDetailsLink.target = '_blank';
      viewDetailsLink.rel = 'noopener';
      viewDetailsLink.setAttribute(
        'aria-label',
        quickView.dataset.ksFullDetailsAriaLabel || viewDetailsLink.textContent
      );
      this.updateQuickViewDetailsLink(quickView);
    }

    updateQuickViewDetailsLink(quickView) {
      const viewDetailsLink = quickView.querySelector('.ks-bundle-quick-view-details');

      if (!(viewDetailsLink instanceof HTMLAnchorElement)) return;

      const variantPicker = quickView.querySelector('variant-picker');
      const variantId = variantPicker?.selectedVariant?.id || quickView.dataset.ksVariantId;
      const productUrl = viewDetailsLink.dataset.ksBundleProductUrl;

      if (!productUrl || !variantId) return;

      const url = new URL(productUrl, window.location.origin);

      url.searchParams.set('variant', String(variantId));
      viewDetailsLink.href = `${url.pathname}${url.search}${url.hash}`;
    }

    renderQuickViewBundleAction(quickView) {
      if (quickView.querySelector('[data-ks-bundle-quick-view-action]')) {
        this.updateQuickViewBundleAction(quickView);
        this.positionQuickViewDetailsLink(quickView);
        return;
      }

      const productInfoBlocks = quickView.querySelector('.product-info__block-list');
      const buyButtonsBlock = quickView.querySelector('[data-block-type="buy-buttons"]');
      const actionLabel = quickView.dataset.ksAddLabel;

      if (!(productInfoBlocks instanceof HTMLElement) || !actionLabel) return;

      const actionWrapper = document.createElement('div');
      const actionButton = document.createElement('button');

      actionWrapper.className = 'product-info__block-item ks-bundle-quick-view-action';
      actionWrapper.dataset.ksBundleQuickViewAction = '';
      actionButton.className = 'button w-full';
      actionButton.type = 'button';
      actionButton.dataset.ksBundleQuickViewButton = '';
      actionButton.addEventListener('click', () => this.addQuickViewBundleItem(quickView));
      actionWrapper.append(actionButton);

      if (buyButtonsBlock instanceof HTMLElement) {
        buyButtonsBlock.insertAdjacentElement('afterend', actionWrapper);
      } else {
        productInfoBlocks.append(actionWrapper);
      }

      this.positionQuickViewDetailsLink(quickView);
      this.updateQuickViewBundleAction(quickView);
    }

    positionQuickViewDetailsLink(quickView) {
      const actionWrapper = quickView.querySelector('[data-ks-bundle-quick-view-action]');
      const viewDetailsLink = quickView.querySelector('.ks-bundle-quick-view-details');

      if (!(actionWrapper instanceof HTMLElement)) return;

      // Use the current product's description from the fetched quick-view content.
      let description = quickView.querySelector('[data-ks-bundle-description]');
      const descriptionSource = quickView.querySelector('[data-ks-bundle-description-source]');

      if (!description && descriptionSource instanceof HTMLTemplateElement) {
        description = descriptionSource.content.firstElementChild?.cloneNode(true);
      }

      if (description && actionWrapper.nextElementSibling !== description) {
        actionWrapper.insertAdjacentElement('afterend', description);
      }

      if (!(viewDetailsLink instanceof HTMLAnchorElement)) return;
      const lastContent = description || actionWrapper;
      if (lastContent.nextElementSibling === viewDetailsLink) return;

      lastContent.insertAdjacentElement('afterend', viewDetailsLink);
    }

    updateQuickViewBundleAction(quickView) {
      const actionButton = quickView.querySelector('[data-ks-bundle-quick-view-button]');

      if (!(actionButton instanceof HTMLButtonElement)) return;

      if (quickView.dataset.ksBundleFeedback === 'added') {
        const addedLabel = quickView.dataset.ksAddedLabel || 'Added to bundle';

        if (actionButton.textContent !== '✓') {
          actionButton.textContent = '✓';
        }

        if (actionButton.getAttribute('aria-label') !== addedLabel) {
          actionButton.setAttribute('aria-label', addedLabel);
        }

        if (!actionButton.disabled) {
          actionButton.disabled = true;
        }

        return;
      }

      const variantPicker = quickView.querySelector('variant-picker');
      const selectedVariant = variantPicker?.selectedVariant;
      const hasSelectedVariant = !variantPicker || Boolean(selectedVariant?.id);
      const isSoldOut = variantPicker
        ? selectedVariant?.available === false
        : quickView.dataset.ksVariantAvailable === 'false';
      const bundleItem = this.getBundleItemFromQuickView(quickView);
      let actionLabel = quickView.dataset.ksAddLabel || 'Add to bundle';

      if (isSoldOut) {
        actionLabel = quickView.dataset.ksSoldOutLabel || 'Sold out';
      } else if (!hasSelectedVariant) {
        actionLabel = quickView.dataset.ksUnavailableLabel || 'Unavailable';
      }

      if (actionButton.textContent !== actionLabel) {
        actionButton.textContent = actionLabel;
      }

      actionButton.removeAttribute('aria-label');

      if (actionButton.disabled !== !bundleItem) {
        actionButton.disabled = !bundleItem;
      }
    }

    async addQuickViewBundleItem(quickView) {
      const bundleItem = this.getBundleItemFromQuickView(quickView);
      const actionButton = quickView.querySelector('[data-ks-bundle-quick-view-button]');

      if (!bundleItem) return;

      quickView.dataset.ksBundleFeedback = 'added';

      if (actionButton instanceof HTMLButtonElement) {
        actionButton.textContent = '✓';
        actionButton.setAttribute('aria-label', quickView.dataset.ksAddedLabel || 'Added to bundle');
        actionButton.disabled = true;
      }

      this.addBundleItemToState(bundleItem);
      await new Promise((resolve) => window.setTimeout(resolve, 650));
      await quickView.hide();
      delete quickView.dataset.ksBundleFeedback;
      this.updateQuickViewBundleAction(quickView);
    }

    getBundleItemFromQuickView(quickView) {
      const initialItem = this.getBundleItemFromElement(quickView);
      const variantPicker = quickView.querySelector('variant-picker');
      const selectedVariant = variantPicker?.selectedVariant;
      const quantityInput = quickView.querySelector('form[action*="/cart/add"] [name="quantity"]');
      const selectedQuantity = Math.max(1, Math.floor(Number(quantityInput?.value) || 1));

      if (!initialItem) return null;
      if (variantPicker && (!selectedVariant?.id || selectedVariant.available === false)) return null;
      if (!variantPicker && quickView.dataset.ksVariantAvailable === 'false') return null;
      if (!variantPicker) {
        return { ...initialItem, quantity: selectedQuantity };
      }

      const featuredImage = selectedVariant.featured_image;
      const featuredMediaImage = selectedVariant.featured_media?.preview_image;
      const selectedImageUrl = typeof featuredImage === 'string'
        ? featuredImage
        : featuredImage?.src || featuredMediaImage?.src || initialItem.imageUrl;
      const selectedImageAlt = typeof featuredImage === 'object'
        ? featuredImage?.alt || featuredMediaImage?.alt || initialItem.imageAlt
        : initialItem.imageAlt;

      return {
        ...initialItem,
        variantId: String(selectedVariant.id),
        variantTitle: String(selectedVariant.title || ''),
        selectedOptions: Array.isArray(selectedVariant.options)
          ? selectedVariant.options.map((option) => String(option))
          : [],
        imageUrl: selectedImageUrl,
        imageAlt: selectedImageAlt,
        unitPrice: Math.max(0, Math.round(Number(selectedVariant.price) || 0)),
        quantity: selectedQuantity
      };
    }

    updateBundleProgress(itemCount) {
      const progresses = Array.from(this.querySelectorAll('[data-ks-bundle-progress]'));

      if (progresses.length === 0) return 0;

      const normalizedItemCount = Math.max(0, Math.floor(Number(itemCount) || 0));
      let activeDiscountPercentage = 0;

      progresses.forEach((progress) => {
        if (!(progress instanceof HTMLElement)) return;

        const tiers = Array.from(progress.querySelectorAll('[data-ks-bundle-tier]'));
        const progressMessage = progress.querySelector('[data-ks-bundle-progress-message]');
        const tierThresholds = tiers.map((tier) => Number(tier.dataset.ksBundleTier));
        const maximumTier = Math.max(...tierThresholds);
        const cappedItemCount = Math.min(normalizedItemCount, maximumTier);
        const nextTierIndex = tierThresholds.findIndex((threshold) => threshold > normalizedItemCount);
        const nextTier = tiers[nextTierIndex];
        const reachedTier = tiers.findLast((tier) => Number(tier.dataset.ksBundleTier) <= normalizedItemCount);
        let progressWidth = 100;

        if (nextTierIndex >= 0) {
          const previousThreshold = nextTierIndex === 0 ? 0 : tierThresholds[nextTierIndex - 1];
          const nextThreshold = tierThresholds[nextTierIndex];
          const previousPosition = (nextTierIndex / tiers.length) * 100;
          const nextPosition = ((nextTierIndex + 1) / tiers.length) * 100;
          const tierProgress = (cappedItemCount - previousThreshold) / (nextThreshold - previousThreshold);

          progressWidth = previousPosition + (nextPosition - previousPosition) * tierProgress;
        }

        progress.style.setProperty('--ks-bundle-progress-width', `${progressWidth}%`);
        progress.setAttribute('aria-valuenow', String(cappedItemCount));

        tiers.forEach((tier) => {
          const isReached = Number(tier.dataset.ksBundleTier) <= normalizedItemCount;
          tier.setAttribute('data-ks-bundle-tier-reached', String(isReached));
        });

        let message = '';
        let messageTemplate = '';
        let messageAmount = 0;
        let messagePercentage = 0;

        if (nextTier instanceof HTMLElement) {
          messageTemplate = progress.dataset.ksProgressMessage;
          messageAmount = Number(nextTier.dataset.ksBundleTier) - normalizedItemCount;
          messagePercentage = nextTier.dataset.ksBundleDiscount;
          message = this.formatBundleProgressMessage(
            messageTemplate,
            messageAmount,
            messagePercentage
          );
        } else if (reachedTier instanceof HTMLElement) {
          messageTemplate = progress.dataset.ksProgressCompleteMessage;
          messagePercentage = reachedTier.dataset.ksBundleDiscount;
          message = this.formatBundleProgressMessage(
            messageTemplate,
            messageAmount,
            messagePercentage
          );
        }

        if (progressMessage instanceof HTMLElement) {
          this.renderBundleProgressMessage(progressMessage, messageTemplate, messageAmount, messagePercentage);
        }

        progress.setAttribute('aria-valuetext', message);
        activeDiscountPercentage = Number(reachedTier?.dataset.ksBundleDiscount) || activeDiscountPercentage;
      });

      return activeDiscountPercentage;
    }

    formatBundleProgressMessage(template, amount, percentage) {
      return String(template || '')
        .replaceAll('[amount]', String(amount))
        .replaceAll('[percentage]', `${percentage}%`);
    }

    renderBundleProgressMessage(element, template, amount, percentage) {
      const messageParts = String(template || '').split(/(\[amount\]|\[percentage\])/g);
      const fragment = document.createDocumentFragment();

      messageParts.forEach((part) => {
        if (part === '[amount]' || part === '[percentage]') {
          const value = document.createElement('strong');

          value.className = 'ks-bundle-progress-message-value';
          value.textContent = part === '[amount]' ? String(amount) : `${percentage}%`;
          fragment.append(value);
        } else if (part) {
          fragment.append(document.createTextNode(part));
        }
      });

      element.replaceChildren(fragment);
    }

    updateBundleSummary(itemCount, subtotalCents) {
      const normalizedItemCount = Math.max(0, Math.floor(Number(itemCount) || 0));
      const discountPercentage = this.updateBundleProgress(normalizedItemCount);

      this.querySelectorAll('[data-ks-bundle-item-count]').forEach((element) => {
        element.textContent = String(normalizedItemCount);
      });

      const bundleActionButtons = new Set(this.querySelectorAll('[data-ks-bundle-show-contents]'));

      this.getBundleSubmitButtons().forEach((button) => {
        bundleActionButtons.add(button);
      });

      bundleActionButtons.forEach((button) => {
        if (button instanceof HTMLButtonElement) {
          const isSubmitButton = button.matches('[data-ks-bundle-submit]');

          button.disabled = normalizedItemCount === 0 || (isSubmitButton && this.bundleIsSubmitting);
        }
      });

      this.updateBundleTotals(subtotalCents, discountPercentage);
    }

    updateBundleTotals(subtotalCents, discountPercentage) {
      const normalizedSubtotal = Math.max(0, Math.round(Number(subtotalCents) || 0));
      const normalizedDiscount = Math.max(0, Number(discountPercentage) || 0);
      const savings = Math.round(normalizedSubtotal * (normalizedDiscount / 100));
      const finalTotal = normalizedSubtotal - savings;
      const hasSavings = savings > 0;

      const bundleTotals = new Set(this.querySelectorAll('[data-ks-bundle-totals]'));

      this.bundleContentsModal?.querySelectorAll('[data-ks-bundle-totals]').forEach((totals) => {
        bundleTotals.add(totals);
      });

      bundleTotals.forEach((totals) => {
        const originalTotalElement = totals.querySelector('[data-ks-bundle-original-total]');
        const finalTotalElement = totals.querySelector('[data-ks-bundle-final-total]');
        const savingsElement = totals.querySelector('[data-ks-bundle-savings]');
        const savingsPercentageElement = totals.querySelector('[data-ks-bundle-savings-percentage]');

        if (originalTotalElement instanceof HTMLElement) {
          originalTotalElement.textContent = this.formatBundleMoney(normalizedSubtotal);
          originalTotalElement.hidden = !hasSavings;
        }

        if (finalTotalElement instanceof HTMLElement) {
          finalTotalElement.textContent = this.formatBundleMoney(finalTotal);
        }

        if (savingsElement instanceof HTMLElement) {
          savingsElement.textContent = this.formatBundleMoney(savings);
        }

        if (savingsPercentageElement instanceof HTMLElement) {
          savingsPercentageElement.textContent = String(hasSavings ? normalizedDiscount : 0);
        }
      });
    }

    formatBundleMoney(cents) {
      const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
      const currency = this.getBundleCurrency();
      const currencyDisplay = window.themeVariables?.settings?.currencyCodeEnabled ? 'code' : 'narrowSymbol';

      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay
      }).format(cents / 100);
    }

    getBundleCurrency() {
      return window.Shopify?.currency?.active || 'AUD';
    }
  }

  // Register the custom element once, including during theme editor reloads.
  customElements.define('ks-bundle-builder', KsBundleBuilder);
}
